import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Stack, Typography, Divider, InputAdornment } from '@mui/material';

import { PaperCard } from 'components/Card';
import { FormProvider, TextField, DateTimeInput, NumericFormat, MultiSelect, Select, CardAutocomplete, Checkbox } from 'components/Form';
import { useCompetenceAreas } from 'api/getCompetenceAreas';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AdditionalPeriods } from 'components/AdditionalPeriod/AdditionalPeriod';
import { useWatch } from 'react-hook-form';
import { NutsCodes } from 'components/NutsCodes/NutsCodes';
import { PlacesField } from 'components/PlaceAutocomplete/PlacesField';
import { useAuth } from 'lib/auth';



export const AdditionalDetails = ({ handleCloseModal, methods, onSubmit, companyID, buyerFlow, title = 'Adding Advertisement' }) => {
  const { user, account } = useAuth();
  const isBuyer = !!buyerFlow;
  const [ additionalPeriods, setAdditionalPeriods ] = useState(() => methods.getValues('additionalPeriods') || []);
  const [ initialPeriod, setInitialPeriod ] = useState(() => [
    {
      label: "Initial",
      startDate: methods.getValues('startDate') || null,
      endDate: methods.getValues('endDate') || null,
      status: "used",
    }
  ]);
  const [ selectedCompetenceAreas , setSelectedCompetenceAreas ] = useState(() => methods.getValues('competenceAreas') || []);
  const [ competencesArray, setCompetencesArray ] = useState([]);
  const [ lastDayToRespond, setLastDayToRespond ] = useState(() => methods.getValues('lastDayToRespond') || null);
  const [ questionsEndDate, setQuestionsEndDate ] = useState(() => methods.getValues('questionsEndDate') || null);

  const [selectedNodes, setSelectedNodes] = useState(() => methods.getValues('locations') || []);

  const { data: competences } = useCompetenceAreas();

  const handleAdditionalPeriods = (value) => {
    setAdditionalPeriods(value);
    methods.setValue("additionalPeriods", value);
  }

  const handleInitialPeriod = (value) => {
    setInitialPeriod(value);
    methods.setValue("startDate", value[0].startDate);
    methods.setValue("endDate", value[0].endDate);
  }

  const handleCompetenceAreasChange = (value) => {
    setSelectedCompetenceAreas(value);
    methods.setValue("competenceAreas", value);
    if (value.length > 0) {
      methods.trigger("competenceAreas");
    }
  };

  useEffect(() => {
    if (competences && Array.isArray(competences.competences)) {
      const allCompetences = competences.competences.map(item => ({
        value: item.id,
        label: item.name,
        ...item,
      }));

      setCompetencesArray(allCompetences);
    }
  }, [competences]);

  const handleDateChange = (date, field) => {
    if (field === 'lastDayToRespond') {
      setLastDayToRespond(date);
    }
    if (field === 'questionsEndDate') {
      setQuestionsEndDate(date);
    }

    if (methods !== null) {
      methods.setValue(field, date);
    }

    if(date !== null) {
      methods.trigger(field);
    }
    if (field === 'lastDayToRespond' && methods.getValues('questionsEndDate')) {
      methods.trigger('questionsEndDate');
    }
  };

  const applicationTypeOptions = [
    ...(isBuyer ? [] : [{label: "Email", value: "email"}, {label: "Website URL", value: "url"}]),
    {label: "In app", value: "in_app"},
  ];

  const { control, setValue } = methods;

  const applicationTypeValue = useWatch({
    control,
    name: 'applicationType',
  });

  const selectedSupplierId = useWatch({
    control,
    name: 'supplierID',
  });

  const usesNuts = useWatch({
    control,
    name: 'usesNuts',
  });

  useEffect(() => {
  if (isBuyer && applicationTypeValue !== 'in_app') {
    setValue('applicationType', 'in_app');
    return;
  }
  if (applicationTypeValue === 'email') {
    setValue('applicationEmail', user?.email, { shouldValidate: true });
  } else {
    setValue('applicationEmail', '');
  }
}, [applicationTypeValue, setValue, isBuyer]);

  const AdditionalDetailsContent = (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        background: '#F3F4F6',
        justifyContent: 'center',
        display: 'flex',
        zIndex: (theme) => theme.zIndex.modal,
        alignItems: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          position: 'relative',
          width: '100%',
          // height: `calc(100% - 8 * 26.25px)`,
          height: `calc(100% - 69px)`,
          // marginTop: 26.25,
          marginTop: 8.625,
          overflowY: 'auto',
          backgroundColor: '#fff',
        }}
      >
        <FormProvider methods={methods} onSubmit={onSubmit}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <PaperCard
            height={69}
            sx={{
              background: '#fff',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              px: 4,
              py: 2,
              border: 0,
              borderRadius: 0,
              boxShadow: 26,
              display: 'flex',
              boxSizing: 'border-box',
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography>{title}</Typography>
              <Box
                sx={{
                  width: 165,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  size="medium"
                  onClick={handleCloseModal}
                  variant="contained"
                  sx={({ palette }) => ({
                    width: 82,
                    boxShadow: 25,
                    color: palette.primary.main,
                    backgroundColor: '#fff',
                  })}
                >
                  Cancel
                </Button>
                <Button
                  size="medium"
                  type="submit"
                  // onClick={onSubmit}
                  variant="contained"
                  color="primary"
                  sx={{ width: 67 }}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          </PaperCard>

          <Stack
            direction="row"
            width="100%"
            minHeight={`calc(100vh - 69px)`}
            height="100%"
            bgcolor={'#fff'}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack width={'50%'} direction="column" px={8} py={4}>
              <Stack direction={"row"} gap={2}>
                <TextField
                  name="buyerName"
                  label="Buyer"
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                  disabled
                  value={methods.getValues("buyerName")}
                />
                {/* <TextField
                  name="agreementName"
                  label="Agreement"
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                  disabled
                  value={methods.getValues("agreementName")}
                /> */}
              </Stack>
              {!isBuyer && (
                <Checkbox
                  name="hideBuyer"
                  label="Hide buyer name and logo from other companies"
                />
              )}
              <TextField
                name="headline"
                label="Headline"
                placeholder="Please enter Headline"
                required
                type="text"
                sx={{ mb: 2 }}
                fullWidth
              />
              <Stack direction={"row"} gap={2}>
                <TextField
                  name="role"
                  label="Role"
                  placeholder="Please enter Role"
                  required
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                />
                <TextField
                  name="experienceLevel"
                  label="Experience level"
                  placeholder="Please enter Experience level"
                  required
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                />
              </Stack>
              <Stack direction={"row"} gap={2}>
                <Select
                  name="type"
                  label="Type"
                  options={[
                    { label: "On site", value: "On site" },
                    { label: "Hybrid", value: "Hybrid" },
                    { label: "Remote", value: "Remote" },
                  ]}
                  placeholder="Please select type"
                  wrapperSx={{ mb: 2 }}
                  required
                  fullWidth
                />
                <NumericFormat
                  name="hoursPerWeek"
                  label="Hours per week"
                  placeholder="Please enter hours per week"
                  type="input"
                  sx={{ mb: 2}}
                  wrapperSx={{width: 1}}
                  labelSx={{mb: -0.5}}
                  required
                />
              </Stack>
              <TextField
                name="description"
                label="Assignment description"
                placeholder="Please enter assignment description"
                type="text"
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiInputBase-root, .MuiInputBase-input': {
                    p: 0,
                  },
                }}
                multiline
                minRows={2}
                required
              />
            </Stack>

            <Stack width={'50%'} direction="column" px={8} py={4}>
              <Stack direction={"row"} gap={2}>
                <NumericFormat
                  name="maxRate"
                  label="Max Rate"
                  placeholder="Please enter Max Rate"
                  type="input"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                      >
                        SEK/h
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2}}
                  wrapperSx={{width: 1}}
                  labelSx={{mb: -0.5}}
                  required
                />
                <TextField
                  name="referenceNumber"
                  label="Reference number"
                  placeholder="Please enter Reference number"
                  required
                  type="text"
                  sx={{ mb: 2}}
                  fullWidth
                />
              </Stack>

              <PlacesField name="places" label="Locations" required fullWidth wrapperSx={{mb: 2}} />
              {usesNuts && (
                <NutsCodes
                  name="locations"
                  label="NUTS codes"
                  required
                  fullWidth
                  wrapperSx={{mb: 2}}
                  methods={methods}
                  selectedItems={selectedNodes}
                />
              )}
              <MultiSelect multiple value={selectedCompetenceAreas} onChange={handleCompetenceAreasChange} placeholder="Please select" required fullWidth name="competenceAreas" label="Competence Areas" options={competencesArray} wrapperSx={{ mb: 2 }} />
              <AdditionalPeriods initialPeriod={initialPeriod} setInitialPeriod={handleInitialPeriod} additionalPeriods={additionalPeriods} handleAdditionalPeriods={handleAdditionalPeriods} methods={methods} tender />
              <Stack direction={"row"} gap={2} sx={{mt: 2}}>
                <Select
                  name="applicationType"
                  label="Application Type"
                  options={applicationTypeOptions}
                  placeholder="Please select application type"
                  wrapperSx={{mb:1}}
                  required
                  fullWidth
                />
                <DateTimeInput
                  name="lastDayToRespond"
                  label="Last day to respond"
                  required
                  fullWidth
                  minDate={dayjs()}
                  maxDate={dayjs().add(60, 'day')}
                  value={dayjs(lastDayToRespond)}
                  onChange={(date) => handleDateChange(date, "lastDayToRespond")}
                />
                <DateTimeInput
                  name="questionsEndDate"
                  label="End date for questions"
                  required
                  fullWidth
                  minDate={dayjs()}
                  maxDate={lastDayToRespond ? dayjs(lastDayToRespond) : dayjs().add(60, 'day')}
                  value={dayjs(questionsEndDate)}
                  onChange={(date) => handleDateChange(date, "questionsEndDate")}
                />
              </Stack>
              {applicationTypeValue === 'email' && (
                <TextField
                  name="applicationEmail"
                  label="Application email"
                  placeholder="Please enter Application email"
                  required
                  type="email"
                  autoComplete="email"
                  sx={{ mb: 2}}
                  fullWidth
                />
              )}

              {applicationTypeValue === 'url' && (
                <TextField
                  name="applicationURL"
                  label="Application URL"
                  placeholder="Please enter Application URL"
                  required
                  type="text"
                  sx={{ mb: 2}}
                  fullWidth
                />
              )}
            </Stack>
          </Stack>
          </LocalizationProvider>
        </FormProvider>
      </Stack>
    </Box>
  );

  return ReactDOM.createPortal(AdditionalDetailsContent, document.body);
};
